import React from 'react'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {StaticDatePicker} from '@mui/x-date-pickers/StaticDatePicker'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
  Chip
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {format, isBefore, isSameDay, startOfDay} from 'date-fns'
import enLocale from 'date-fns/locale/en-US'
import heLocale from 'date-fns/locale/he'
import arLocale from 'date-fns/locale/ar-SA'
import TimelineEventTemplate from 'web-common/views/timeline/TimelineEventTemplate'
import {withTranslation, type WithTranslation} from 'react-i18next'
import {ChatAdapter} from '@fnd/timeline'
import {AssistantEventProducer} from '../AssistantEventProducer'
import {Calendar} from 'iconsax-reactjs'

export interface DateTimeEntry {
  date: Date
  selectedTimeSlots: string[]
}

interface DateTimePickerProps {
  buttonLabel: string
  position: 'flex-start' | 'center' | 'flex-end'
  producer: AssistantEventProducer
}

interface DateTimePickerState {
  selectedDateEntries: DateTimeEntry[]
  currentEditingDateIndex: number | null
  temporarySelectedTimeSlots: string[]
  customTimeValue: Date | null
  isDateTimeDialogOpen: boolean
  customTimeCheckboxSelected: boolean
}

interface InternalProps extends DateTimePickerProps, WithTranslation {}

const getLocaleByLanguage = (lang: string) => {
  switch (lang) {
    case 'he':
      return heLocale
    case 'ar':
      return arLocale
    default:
      return enLocale
  }
}

class DateTimePicker extends TimelineEventTemplate<
  InternalProps,
  DateTimePickerState
> {
  state: DateTimePickerState = {
    selectedDateEntries: [],
    currentEditingDateIndex: null,
    temporarySelectedTimeSlots: [],
    customTimeValue: null,
    isDateTimeDialogOpen: false,
    customTimeCheckboxSelected: false
  }

  openDateTimeDialog = () => {
    this.setState({
      isDateTimeDialogOpen: true,
      selectedDateEntries: [],
      currentEditingDateIndex: null,
      temporarySelectedTimeSlots: []
    })
  }

  closeDateTimeDialog = () => {
    this.setState({
      isDateTimeDialogOpen: false,
      customTimeValue: null,
      currentEditingDateIndex: null,
      temporarySelectedTimeSlots: [],
      customTimeCheckboxSelected: false
    })
  }

  onDateSelected = (date: Date | null) => {
    if (!date) return

    const existingDateIndex = this.state.selectedDateEntries.findIndex(
      (entry) => isSameDay(entry.date, date)
    )

    if (existingDateIndex === -1) {
      const newDateEntry: DateTimeEntry = {date, selectedTimeSlots: []}
      this.setState({
        selectedDateEntries: [...this.state.selectedDateEntries, newDateEntry],
        currentEditingDateIndex: this.state.selectedDateEntries.length,
        temporarySelectedTimeSlots: []
      })
    } else {
      this.setState({
        currentEditingDateIndex: existingDateIndex,
        temporarySelectedTimeSlots: [
          ...this.state.selectedDateEntries[existingDateIndex].selectedTimeSlots
        ]
      })
    }
  }

  startEditingDate = (dateIndex: number) => {
    this.setState({
      currentEditingDateIndex: dateIndex,
      temporarySelectedTimeSlots: [
        ...this.state.selectedDateEntries[dateIndex].selectedTimeSlots
      ]
    })
  }

  removeDateEntry = (dateIndex: number) => {
    const updatedDateEntries = this.state.selectedDateEntries.filter(
      (_, index) => index !== dateIndex
    )
    this.setState({selectedDateEntries: updatedDateEntries})
  }

  toggleTimeSlotSelection = (timeSlot: string, isSelected: boolean) => {
    if (isSelected) {
      this.setState({
        temporarySelectedTimeSlots: [
          ...this.state.temporarySelectedTimeSlots,
          timeSlot
        ]
      })
    } else {
      this.setState({
        temporarySelectedTimeSlots:
          this.state.temporarySelectedTimeSlots.filter(
            (slot) => slot !== timeSlot
          )
      })
    }
  }

  addCustomTimeSlot = () => {
    if (this.state.customTimeValue) {
      const formattedCustomTime = format(this.state.customTimeValue, 'HH:mm')
      if (
        !this.state.temporarySelectedTimeSlots.includes(formattedCustomTime)
      ) {
        this.setState({
          temporarySelectedTimeSlots: [
            ...this.state.temporarySelectedTimeSlots,
            formattedCustomTime
          ],
          customTimeValue: null
        })
      }
    }
  }

  deleteCustomTimeSlot = (slot: string) => {
    this.setState((prevState) => ({
      temporarySelectedTimeSlots: prevState.temporarySelectedTimeSlots.filter(
        (s) => s !== slot
      )
    }))
  }

  confirmTimeSlotSelection = () => {
    const updatedDateEntries = [...this.state.selectedDateEntries]
    updatedDateEntries[this.state.currentEditingDateIndex!].selectedTimeSlots =
      this.state.temporarySelectedTimeSlots

    this.setState({
      selectedDateEntries: updatedDateEntries,
      currentEditingDateIndex: null,
      temporarySelectedTimeSlots: [],
      customTimeCheckboxSelected: false
    })
  }

  cancelTimeSlotSelection = () => {
    this.setState({
      currentEditingDateIndex: null,
      temporarySelectedTimeSlots: [],
      customTimeCheckboxSelected: false
    })
  }

  shouldDisableDate = (date: Date): boolean => {
    const today = startOfDay(new Date())
    const isPast = isBefore(date, today)
    const isAlreadyPicked = this.state.selectedDateEntries.some((entry) =>
      isSameDay(entry.date, date)
    )
    return isPast || isAlreadyPicked
  }

  async applySelectedDates() {
    if (this.state.selectedDateEntries.length > 0) {
      const firstEntry = this.state.selectedDateEntries[0]
      const timestamp = firstEntry.date.getTime()

      const message = this.state.selectedDateEntries
        .map((entry) => {
          const formattedDate = format(entry.date, 'PP', {
            locale: getLocaleByLanguage(this.props.i18n.language)
          })
          const timeSlots = entry.selectedTimeSlots.join(', ')
          return `${formattedDate} ${timeSlots}`
        })
        .join('\n')

      await this.props.producer.createEvent({
        message: message,
        adapterId: ChatAdapter.id,
        data: {
          metadata: {
            action: 'SHARED_DATE',
            eventDate: timestamp
          }
        }
      })
    }

    this.setState({
      selectedDateEntries: [],
      isDateTimeDialogOpen: false,
      customTimeValue: null,
      currentEditingDateIndex: null,
      temporarySelectedTimeSlots: [],
      customTimeCheckboxSelected: false
    })
  }

  renderTimeSlotSelectionMenu(dateIndex: number) {
    const predefinedTimeSlots = [
      '08:00 - 12:00',
      '12:00 - 16:00',
      '16:00 - 20:00',
      this.props.t('fnd-assistant-dialog-date-slot-after-20')
    ]

    const dateEntry = this.state.selectedDateEntries[dateIndex]
    const date = dateEntry.date

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: {xs: 'row', sm: 'column'},
          gap: 2,
          alignItems: {xs: 'flex-start', sm: 'stretch'},
          paddingBottom: {xs: 0, sm: 2}
        }}
      >
        <Box sx={{flex: 1}}>
          <Typography variant="body1" marginTop={2.5}>
            {format(date, 'PP', {
              locale: getLocaleByLanguage(this.props.i18n.language)
            })}
          </Typography>
          <FormGroup>
            {predefinedTimeSlots.map((slot) => (
              <FormControlLabel
                key={slot}
                control={
                  <Checkbox
                    size="small"
                    checked={this.state.temporarySelectedTimeSlots.includes(
                      slot
                    )}
                    onChange={(e) =>
                      this.toggleTimeSlotSelection(slot, e.target.checked)
                    }
                  />
                }
                label={slot}
              />
            ))}
          </FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={this.state.customTimeCheckboxSelected}
                onChange={(e) =>
                  this.setState({customTimeCheckboxSelected: e.target.checked})
                }
              />
            }
            label={this.props.t('fnd-assistant-dialog-date-slot-custom')}
          />
          {this.state.customTimeCheckboxSelected && (
            <>
              <Box sx={{display: 'flex', alignItems: 'center', marginTop: 2}}>
                <TimePicker
                  value={this.state.customTimeValue}
                  views={['hours']}
                  ampm={false}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{width: 120}} />
                  )}
                  onChange={(time: Date | null) =>
                    this.setState({customTimeValue: time})
                  }
                />
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{marginLeft: 1}}
                  disabled={!this.state.customTimeValue}
                  onClick={this.addCustomTimeSlot}
                >
                  {this.props.t('fnd-assistant-common-add')}
                </Button>
              </Box>
              <Box
                sx={{display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 2}}
              >
                {this.state.temporarySelectedTimeSlots
                  .filter((slot) => !predefinedTimeSlots.includes(slot))
                  .map((customSlot, index) => (
                    <Chip
                      key={index}
                      label={customSlot}
                      size="small"
                      onDelete={() => this.deleteCustomTimeSlot(customSlot)}
                    />
                  ))}
              </Box>
            </>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column-reverse', sm: 'row'},
            gap: 1,
            marginTop: {xs: 2.5, sm: 0},
            alignItems: {xs: 'flex-end', sm: 'flex-start'}
          }}
        >
          <Button
            variant="contained"
            color="inherit"
            size="small"
            sx={{minWidth: 80}}
            onClick={this.cancelTimeSlotSelection}
          >
            {this.props.t('fnd-assistant-common-cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{minWidth: 80}}
            onClick={this.confirmTimeSlotSelection}
          >
            {this.props.t('fnd-assistant-common-select')}
          </Button>
        </Box>
      </Box>
    )
  }

  render() {
    const locale = getLocaleByLanguage(this.props.i18n.language)

    return (
      <LocalizationProvider
        key={this.props.i18n.language}
        dateAdapter={AdapterDateFns}
        adapterLocale={locale}
      >
        <Stack spacing={2} alignItems={this.props.position} marginTop={1}>
          <Button
            startIcon={<Calendar />}
            variant="contained"
            color="primary"
            size="medium"
            sx={{borderRadius: 3}}
            disabled={this.props.producer.hasDemandId()}
            onClick={this.openDateTimeDialog}
          >
            {this.props.buttonLabel}
          </Button>
          <Dialog
            open={this.state.isDateTimeDialogOpen}
            onClose={this.closeDateTimeDialog}
            PaperProps={{
              sx: {
                width: '100%',
                maxWidth: {xs: '95%', sm: 'fit-content'},
                margin: {xs: 1, sm: 2}
              }
            }}
          >
            <DialogTitle>
              {this.props.t('fnd-assistant-dialog-date-title')}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: {xs: 'column', sm: 'row'},
                  gap: {xs: 0, sm: 3},
                  alignItems: {xs: 'stretch', sm: 'flex-start'}
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: {xs: '100%', sm: 'auto'},
                    maxWidth: {xs: '100%', sm: '320px'},
                    overflowX: 'hidden',
                    '& .MuiPickersLayout-root': {
                      width: '100%',
                      overflowX: 'hidden'
                    }
                  }}
                >
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    onChange={this.onDateSelected}
                    renderInput={(params) => <TextField {...params} />}
                    value={null}
                    shouldDisableDate={this.shouldDisableDate}
                  />
                </Box>
                <Box sx={{flex: 1, overflowY: 'auto'}}>
                  {this.state.currentEditingDateIndex === null ? (
                    <List>
                      {this.state.selectedDateEntries.map((entry, index) => (
                        <ListItem key={index}>
                          <Box sx={{width: '100%'}}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Typography variant="body1" marginRight={5}>
                                {format(entry.date, 'PP', {locale})}
                              </Typography>
                              <Box>
                                <IconButton
                                  onClick={() => this.startEditingDate(index)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => this.removeDateEntry(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                marginTop: 1
                              }}
                            >
                              {entry.selectedTimeSlots.map(
                                (time, timeIndex) => (
                                  <Chip
                                    key={timeIndex}
                                    label={time}
                                    size="small"
                                  />
                                )
                              )}
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    this.renderTimeSlotSelectionMenu(
                      this.state.currentEditingDateIndex
                    )
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                color="inherit"
                size="large"
                onClick={this.closeDateTimeDialog}
              >
                {this.props.t('fnd-assistant-common-cancel')}
              </Button>
              <Button
                color="primary"
                disabled={this.state.selectedDateEntries.length === 0}
                onClick={() => this.applySelectedDates()}
              >
                {this.props.t('fnd-assistant-common-apply')}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </LocalizationProvider>
    )
  }
}

export default withTranslation()(DateTimePicker)
